import { Occurrence, Aggregate, CourseOffer } from '@shared/types';
import {
    PublicBlendedCourse,
    PublicClassCourse,
    PublicCourse,
    PublicEducator,
    PublicOccurrence,
} from '@cademy-package/sdk-component-types';
import {
    Course as CourseSchema,
    Offer,
    VirtualLocation,
    Place,
    EventAttendanceModeEnumeration,
    CourseInstance,
} from 'schema-dts';
import { convert as html2PlainText, HtmlToTextOptions } from 'html-to-text';
import { getWorkload } from '../../../services/convertSearchIndexCourse/getCourseSchema';
import { Temporal } from 'temporal-polyfill';

const html2PlainTextOptions: HtmlToTextOptions = {
    selectors: [{ selector: 'img', format: 'skip' }],
};

const fallbackImageUrl =
    'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-1.2.1&q=80&fm=jpg&crop=entropy&cs=tinysrgb&w=1080&fit=max&ixid=eyJhcHBfaWQiOjExODk3Mn0';

type ProviderTypes = {
    '@type': 'EducationalOrganization';
    '@id': string;
    name: string;
    sameAs: string;
    url: string;
};

type SharedSchemaTypes = {
    name: string;
    description: string;
    image: string;
    offers: Offer[];
};

const getOccurrenceLocation = (
    course: PublicClassCourse | PublicBlendedCourse,
    occurrence: PublicOccurrence
): Place | VirtualLocation => {
    const delivery = occurrence.delivery || course.delivery;
    const address = occurrence.address ? occurrence.address : course.address;

    if (delivery === 'online') {
        return {
            '@type': 'VirtualLocation',
            url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/profile`,
        };
    }

    let location: Place = {
        '@type': 'Place',
        name: address?.street || address?.city,
        address: {
            '@type': 'PostalAddress',
            addressLocality: address?.city,
            addressRegion: address?.city,
            streetAddress: address?.street,
            postalCode: address?.postcode,
            addressCountry: {
                '@type': 'Country',
                name: 'GB',
            },
        },
    };

    if (course._geoloc && course._geoloc.lat && course._geoloc.lng) {
        location.geo = {
            '@type': 'GeoCoordinates',
            latitude: course._geoloc.lat,
            longitude: course._geoloc.lng,
        };
    }

    return location;
};

const getOccurrenceAttendanceMode = (
    course: PublicClassCourse | PublicBlendedCourse,
    occurrence: PublicOccurrence
): EventAttendanceModeEnumeration => {
    switch (occurrence.delivery || course.delivery) {
        case 'in-person':
            return 'OfflineEventAttendanceMode';
        case 'on-demand':
        case 'online':
            return 'OnlineEventAttendanceMode';
        default:
            return 'MixedEventAttendanceMode';
    }
};

type GoogleSupportedCourseModes = 'Online' | 'Blended' | 'Onsite';

const getCourseMode = (
    course: PublicCourse,
    occurrence?: PublicOccurrence
): GoogleSupportedCourseModes => {
    if (course.type === 'on-demand') return 'Online';
    if (course.type === 'blended') return 'Blended';

    if (course.type === 'on-request') {
        if (course.available_for_online_delivery && course.available_for_in_person_delivery)
            return 'Blended';
        if (course.available_for_online_delivery) return 'Online';
        return 'Onsite';
    }

    const delivery = occurrence?.delivery || course.delivery;
    if (delivery === 'in-person') return 'Onsite';
    if (delivery === 'hybrid') return 'Blended';
    return 'Online';
};

/*
 * @see https://schema.org/Course
 */
const getInstances = (
    course: PublicCourse,
    educator: PublicEducator,
    occurrences: PublicOccurrence[],
    sharedSchema: SharedSchemaTypes,
    providerSchema: ProviderTypes
): CourseInstance[] | undefined => {
    if (course.type === 'on-demand') {
        return [
            {
                '@type': 'CourseInstance',
                courseMode: 'Online',
                courseWorkload: getWorkload(course.course_duration?.duration),
            },
        ];
    }

    if (course.type === 'on-request') {
        return course.duration_pricing.map((durationOption) => {
            return {
                '@type': 'CourseInstance',
                courseMode: getCourseMode(course),
                courseWorkload: getWorkload(durationOption.duration),
            };
        });
    }

    if (!occurrences.length) {
        return [
            {
                '@type': 'CourseInstance',
                courseMode: getCourseMode(course),
                courseWorkload: 'On-request',
            },
        ];
    }

    return occurrences.map((occurrence) => {
        return {
            '@type': 'CourseInstance',
            courseWorkload: getWorkload((occurrence.end_datetime - occurrence.start_datetime) / 60),
            startDate: Temporal.Instant.from(occurrence.times.start.zoned_datetime).toString(),
            endDate: Temporal.Instant.from(occurrence.times.end.zoned_datetime).toString(),
            courseMode: getCourseMode(course, occurrence),
            location: getOccurrenceLocation(course, occurrence),
            subEvent: {
                ...sharedSchema,
                '@type': 'EducationEvent',
                startDate: Temporal.Instant.from(occurrence.times.start.zoned_datetime).toString(),
                endDate: Temporal.Instant.from(occurrence.times.end.zoned_datetime).toString(),
                eventAttendanceMode: getOccurrenceAttendanceMode(course, occurrence),
                eventStatus: 'https://schema.org/EventScheduled',
                location: getOccurrenceLocation(course, occurrence),
                '@id': `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}/${course.slug}?occurrence=${occurrence._id}`,
                url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}/${course.slug}`,
                performer: providerSchema,
                organizer: providerSchema,
            },
        };
    });
};

const getOffers = (course: PublicCourse): Array<CourseOffer> => {
    if (course.type === 'on-request') {
        if (course.pricing_option.type !== 'price-offers') {
            return [];
        }
        return course.pricing_option.offers;
    }
    return course.offers;
};

const getCourseSchema = (
    course: PublicCourse,
    educator: PublicEducator,
    reviewsAggregate?: Aggregate,
    occurrences?: PublicOccurrence[]
): CourseSchema & { '@context': 'http://schema.org' } => {
    const providerSchema: ProviderTypes = {
        '@type': 'EducationalOrganization',
        '@id': `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}`,
        name: educator.name,
        sameAs: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}`,
        url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}`,
    };

    const sharedSchema: SharedSchemaTypes = {
        name: course.name,
        description:
            course.meta_description || html2PlainText(course.description, html2PlainTextOptions),
        image: course.img_urls?.[0] || course.autoimg?.url || fallbackImageUrl,
        offers: getOffers(course).map((offer): Offer => {
            return {
                '@type': 'Offer',
                '@id': offer._id,
                name: `${course.name} - ${offer.name}`,
                price: offer.price,
                category: offer.price ? 'Paid' : 'Free',
                priceCurrency: course.currency,
                availability: 'InStock',
                validFrom: Temporal.Instant.fromEpochSeconds(course.created_at).toString(),
                url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}/${course.slug}`,
            };
        }),
    };

    const courseSchema: CourseSchema & {
        '@context': 'http://schema.org';
        hasCourseInstance?: CourseInstance[];
    } = {
        '@context': 'http://schema.org',
        '@type': 'Course',
        '@id': `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}/${course.slug}`,
        url: `${process.env.NEXT_PUBLIC_MARKETPLACE_URL}/${educator.slug}/${course.slug}`,
        provider: providerSchema,
        hasCourseInstance: getInstances(
            course,
            educator,
            occurrences || [],
            sharedSchema,
            providerSchema
        ),
        aggregateRating:
            reviewsAggregate && reviewsAggregate.count > 0
                ? {
                      '@type': 'AggregateRating',
                      ratingValue: reviewsAggregate.rating,
                      ratingCount: reviewsAggregate.count,
                  }
                : undefined,
        ...sharedSchema,
    };

    return courseSchema;
};

export default getCourseSchema;
